import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Trash2, Settings, Shield } from "lucide-react";

const API = "/api";

export const AdminPanel = ({ onClose }) => {
  const [allowlist, setAllowlist]         = useState([]);
  const [newEmail, setNewEmail]           = useState("");
  const [settings, setSettings]           = useState(null);
  const [loadingList, setLoadingList]     = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [adding, setAdding]               = useState(false);

  useEffect(() => {
    fetchAllowlist();
    fetchSettings();
  }, []);

  const fetchAllowlist = async () => {
    try {
      const res = await axios.get(`${API}/admin/allowlist`, { withCredentials: true });
      setAllowlist(res.data.allowed_emails || []);
    } catch (e) {
      toast.error("Failed to load allowlist");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/strategy/settings`, { withCredentials: true });
      setSettings(res.data);
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoadingSettings(false);
    }
  };

  const addEmail = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/allowlist/add?email=${encodeURIComponent(newEmail.trim())}`, {}, { withCredentials: true });
      toast.success(`${newEmail} added`);
      setNewEmail("");
      fetchAllowlist();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add email");
    } finally {
      setAdding(false);
    }
  };

  const removeEmail = async (email) => {
    try {
      await axios.post(`${API}/admin/allowlist/remove?email=${encodeURIComponent(email)}`, {}, { withCredentials: true });
      toast.success(`${email} removed`);
      fetchAllowlist();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove email");
    }
  };

  const updateSetting = async (key, value) => {
    try {
      const params = new URLSearchParams();
      params.append(key, value.toString());
      await axios.post(`${API}/strategy/settings?${params.toString()}`, {}, { withCredentials: true });
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success("Setting updated");
    } catch (e) {
      toast.error("Failed to update setting");
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100
    }}>
      <div style={{
        background: "#0a0a0a", border: "1px solid #27272a", borderRadius: "16px",
        width: "480px", maxHeight: "80vh", overflowY: "auto", padding: "28px"
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Shield size={18} color="#a855f7" />
            <span style={{ color: "#fff", fontFamily: "JetBrains Mono, monospace", fontSize: "14px", fontWeight: 600, letterSpacing: "0.1em" }}>ADMIN PANEL</span>
          </div>
          <button onClick={onClose} style={{ color: "#71717a", background: "none", border: "none", cursor: "pointer", fontSize: "20px", lineHeight: 1 }}>×</button>
        </div>

        {/* Access management only. Strategy controls hidden by request. */}

        {/* Allowlist */}
        <div>
          <div style={{ color: "#71717a", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
            <UserPlus size={12} />
            Allowed Users
          </div>

          {/* Add email */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addEmail()}
              placeholder="email@example.com"
              style={{
                flex: 1, background: "#111", border: "1px solid #27272a",
                borderRadius: "8px", padding: "8px 12px", color: "#fff",
                fontFamily: "JetBrains Mono, monospace", fontSize: "12px", outline: "none"
              }}
            />
            <button
              onClick={addEmail}
              disabled={adding || !newEmail.trim()}
              style={{
                padding: "8px 16px", background: "#a855f7", border: "none",
                borderRadius: "8px", color: "#fff", cursor: "pointer",
                fontFamily: "JetBrains Mono, monospace", fontSize: "12px",
                opacity: adding || !newEmail.trim() ? 0.5 : 1
              }}
            >
              Add
            </button>
          </div>

          {/* Email list */}
          <div style={{ space: "4px" }}>
            {loadingList ? (
              <div style={{ color: "#52525b", fontSize: "12px", textAlign: "center", padding: "12px" }}>Loading...</div>
            ) : allowlist.length === 0 ? (
              <div style={{ color: "#52525b", fontSize: "12px", textAlign: "center", padding: "12px" }}>No users added yet</div>
            ) : (
              allowlist.map((email) => (
                <div key={email} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 12px", background: "#111", borderRadius: "8px",
                  border: "1px solid #18181b", marginBottom: "6px"
                }}>
                  <span style={{ color: "#a1a1aa", fontFamily: "JetBrains Mono, monospace", fontSize: "12px" }}>{email}</span>
                  <button
                    onClick={() => removeEmail(email)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#52525b", padding: "2px" }}
                    title="Remove user"
                  >
                    <Trash2 size={14} color="#ef4444" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
